import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import HomePage from './pages/HomePage';
import ProfessorPage from './pages/ProfessorPage';
import ScholarshipPage from './pages/ScholarshipPage';
import TemplatesPage from './pages/TemplatesPage';
import StudyGuidePage from './pages/StudyGuidePage';
import ScholarshipGuidePage from './pages/ScholarshipGuidePage';
import ExpertPage from './pages/ExpertPage';
import AuthModal from './components/AuthModal';
import UpgradeModal from './components/UpgradeModal';
import './App.css';

// ── Path mapping — old setActivePage('id') ko naya navigate() se wire karta hai
const PATH_MAP = {
  home:             '/',
  professors:       '/professors',
  scholarships:     '/scholarships',
  templates:        '/templates',
  studyguide:       '/study-guide',
  scholarshipguide: '/top-scholarships',
  experts:          '/experts',
};

const NAV_LINKS = [
  { id:'home',             label:'Home',             path:'/'                  },
  { id:'professors',       label:'Professors',       path:'/professors'        },
  { id:'scholarships',     label:'Scholarships',     path:'/scholarships'      },
  { id:'templates',        label:'Templates',        path:'/templates'         },
  { id:'studyguide',       label:'Study Guide',      path:'/study-guide'       },
  { id:'scholarshipguide', label:'Top Scholarships', path:'/top-scholarships'  },
];

// ── Payment Success Page — dedicated route ───────────────────
function PaymentSuccessPage() {
  const [status, setStatus] = useState('processing'); // processing | success | error
  const navigate = useNavigate();

  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const planId  = params.get('plan');
    const billing = params.get('billing');
    const gateway = params.get('gateway');

    if (!planId || !billing) {
      navigate('/', { replace: true });
      return;
    }

    const auth = getAuth();

    const confirmPayment = async (firebaseUser) => {
      if (!firebaseUser) {
        console.error('❌ No user logged in');
        setStatus('error');
        setTimeout(() => navigate('/', { replace: true }), 2000);
        return;
      }
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/payments/confirm`,
          { planId, billing, gateway },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data.success) {
          console.log('✅ Plan activated! Credits:', res.data.user.credits);
          setStatus('success');
          setTimeout(() => {
            navigate('/', { replace: true });
            window.location.reload();
          }, 1500);
        } else {
          setStatus('error');
          setTimeout(() => navigate('/', { replace: true }), 2000);
        }
      } catch (err) {
        console.error('❌ Payment confirm failed:', err?.response?.data || err.message);
        setStatus('error');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      unsubscribe();
      confirmPayment(firebaseUser);
    });
  }, [navigate]);

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'calc(100vh - 68px)',background:'#060b14',color:'#94a3b8',fontFamily:'Inter,sans-serif',gap:'16px'}}>
      <div style={{fontSize:'40px'}}>
        {status==='processing' && '⏳'}
        {status==='success'    && '✅'}
        {status==='error'      && '⚠️'}
      </div>
      <div style={{fontSize:'16px'}}>
        {status==='processing' && 'Confirming your payment...'}
        {status==='success'    && 'Plan activated! Redirecting...'}
        {status==='error'      && 'Something went wrong. Redirecting to home...'}
      </div>
    </div>
  );
}

// ── Payment Cancel Page ────────────────────────────────────────
function PaymentCancelPage() {
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => navigate('/', { replace: true }), 2000);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'calc(100vh - 68px)',background:'#060b14',color:'#94a3b8',fontFamily:'Inter,sans-serif',gap:'16px'}}>
      <div style={{fontSize:'40px'}}>ℹ️</div>
      <div style={{fontSize:'16px'}}>Payment cancelled. Redirecting to home...</div>
    </div>
  );
}

// ── Navbar — real <Link> elements (right-click → new tab works) ─
function Navbar({ user, logout, openLogin, openRegister, setShowUpgrade }) {
  const location = useLocation();

  return (
    <nav className="navbar">
      <Link to="/" className="nav-logo" style={{cursor:'pointer',textDecoration:'none'}}>
        <div style={{
          width:'36px',height:'36px',
          background:'linear-gradient(135deg,#6366f1,#10b981)',
          borderRadius:'10px',
          display:'flex',alignItems:'center',justifyContent:'center',
          flexShrink:0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 3L4 7v5c0 5.25 3.5 10.15 8 11.35C16.5 22.15 20 17.25 20 12V7L12 3z" fill="white" fillOpacity="0.95"/>
            <path d="M9 12l2 2 4-4" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <span style={{
          fontFamily:'Space Grotesk,sans-serif',
          fontSize:'17px',
          fontWeight:'700',
          background:'linear-gradient(135deg,#a5b4fc,#34d399)',
          WebkitBackgroundClip:'text',
          WebkitTextFillColor:'transparent',
          letterSpacing:'-0.3px',
        }}>
          FindAScholars
        </span>
      </Link>

      <div className="nav-links">
        {NAV_LINKS.map(p => (
          <Link
            key={p.id}
            to={p.path}
            className={location.pathname === p.path ? 'nav-btn active' : 'nav-btn'}
          >
            {p.label}
          </Link>
        ))}
      </div>

      <div className="nav-right">
        {user ? (
          <>
            <span
              className="credits-badge"
              onClick={() => setShowUpgrade(true)}
              style={{cursor:'pointer'}}
              title={`${user.creditsUsedToday||0}/${user.dailyLimit||0} used today`}
            >
              ⚡ {user.credits} credits
            </span>
            {user.plan !== 'free' && (
              <span style={{fontFamily:'Inter,sans-serif',fontSize:'12px',fontWeight:'600',background:'rgba(99,102,241,0.15)',color:'#818cf8',padding:'4px 10px',borderRadius:'20px',border:'1px solid rgba(99,102,241,0.3)',textTransform:'capitalize'}}>
                {user.plan}
              </span>
            )}
            <span style={{fontFamily:'Inter,sans-serif',fontSize:'14px',color:'#94a3b8'}}>
              {user.name.split(' ')[0]}
            </span>
            <button className="upgrade-btn" onClick={() => setShowUpgrade(true)}>
              Upgrade
            </button>
            <button
              onClick={logout}
              style={{background:'transparent',border:'1px solid #334155',color:'#64748b',padding:'8px 14px',borderRadius:'8px',cursor:'pointer',fontFamily:'Inter,sans-serif',fontSize:'13px'}}
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <button
              onClick={openLogin}
              style={{background:'transparent',border:'1px solid #334155',color:'#94a3b8',padding:'9px 18px',borderRadius:'8px',cursor:'pointer',fontFamily:'Inter,sans-serif',fontSize:'14px',fontWeight:'500'}}
            >
              Login
            </button>
            <button onClick={openRegister} className="upgrade-btn">
              Sign Up Free
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

// ── Routes wrapper — setActivePage compatibility layer ─────────
function AppRoutes({ requireLogin, requireUpgrade }) {
  const navigate = useNavigate();

  // Purani child pages 'professors' jaisi id bhejti hain — yeh
  // function unhe real URL navigation mein convert karta hai
  const setActivePage = (id) => navigate(PATH_MAP[id] || '/');

  return (
    <Routes>
      <Route path="/"                  element={<HomePage setActivePage={setActivePage} setShowUpgrade={requireUpgrade} requireLogin={requireLogin} />} />
      <Route path="/professors"        element={<ProfessorPage requireLogin={requireLogin} requireUpgrade={requireUpgrade} />} />
      <Route path="/scholarships"      element={<ScholarshipPage />} />
      <Route path="/templates"         element={<TemplatesPage requireLogin={requireLogin} requireUpgrade={requireUpgrade} />} />
      <Route path="/study-guide"       element={<StudyGuidePage setActivePage={setActivePage} />} />
      <Route path="/top-scholarships"  element={<ScholarshipGuidePage setActivePage={setActivePage} />} />
      <Route path="/experts"           element={<ExpertPage requireUpgrade={requireUpgrade} />} />
      <Route path="/payment-success"   element={<PaymentSuccessPage />} />
      <Route path="/payment-cancel"    element={<PaymentCancelPage />} />
      <Route path="*"                  element={<HomePage setActivePage={setActivePage} setShowUpgrade={requireUpgrade} requireLogin={requireLogin} />} />
    </Routes>
  );
}

function App() {
  const { user, logout, loading } = useAuth();

  const [showAuth,    setShowAuth]    = useState(false);
  const [authMode,    setAuthMode]    = useState('login');
  const [showUpgrade, setShowUpgrade] = useState(false);

  const openLogin    = () => { setAuthMode('login');    setShowAuth(true); };
  const openRegister = () => { setAuthMode('register'); setShowAuth(true); };
  const requireLogin   = () => { openLogin(); };
  const requireUpgrade = () => {
    if (!user) { openLogin(); return; }
    setShowUpgrade(true);
  };

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#060b14',color:'#64748b',fontFamily:'Inter,sans-serif',fontSize:'16px'}}>
      Loading...
    </div>
  );

  return (
    <BrowserRouter>
      <div className="app">
        <Toaster position="top-right" />

        <Navbar
          user={user}
          logout={logout}
          openLogin={openLogin}
          openRegister={openRegister}
          setShowUpgrade={requireUpgrade}
        />

        <AppRoutes
          requireLogin={requireLogin}
          requireUpgrade={requireUpgrade}
        />

        {/* ===== AUTH MODAL ===== */}
        {showAuth && (
          <AuthModal
            initialMode={authMode}
            onClose={() => setShowAuth(false)}
            onSuccess={(u) => {
              if (u.plan === 'free') {
                setTimeout(() => setShowUpgrade(true), 1500);
              }
            }}
          />
        )}

        {/* ===== UPGRADE MODAL ===== */}
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}

      </div>
    </BrowserRouter>
  );
}

export default App;